import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Plus, Download, Mail, Trash2, X, ArrowRight, FileCheck, Search, Layers, AlignLeft, Eye, BookText, Clock, AlertTriangle, History, Banknote, CheckCircle, FileDown, Pencil, RotateCcw } from 'lucide-react';

const TYPE_LABELS = { angebot: 'Angebot', rechnung: 'Rechnung', gutschrift: 'Gutschrift', stornierung: 'Stornierung', rechnungskorrektur: 'Korrektur' };
const TYPE_COLORS = { angebot: 'text-blue-400 bg-blue-400/10 border-blue-400/20', rechnung: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', gutschrift: 'text-amber-400 bg-amber-400/10 border-amber-400/20', stornierung: 'text-rose-400 bg-rose-400/10 border-rose-400/20', rechnungskorrektur: 'text-purple-400 bg-purple-400/10 border-purple-400/20' };
const OFFER_STATUS = { entwurf: 'Entwurf', gesendet: 'Gesendet', angenommen: 'Angenommen', abgelehnt: 'Abgelehnt', verhandlung: 'Verhandlung' };
const OFFER_COLORS = { entwurf: 'text-slate-400 bg-slate-400/10', gesendet: 'text-blue-400 bg-blue-400/10', angenommen: 'text-emerald-400 bg-emerald-400/10', abgelehnt: 'text-rose-400 bg-rose-400/10', verhandlung: 'text-amber-400 bg-amber-400/10' };

const emptyItem = (taxRate = 19) => ({ description: '', quantity: 1, unit: 'Stk.', unit_price: 0, tax_rate: taxRate, parent_pos: null, sub_pos: null, is_text: false });
const emptyTextItem = () => ({ description: '', quantity: 0, unit: '', unit_price: 0, tax_rate: 19, parent_pos: null, sub_pos: null, is_text: true });
const emptyForm = () => ({ type: 'rechnung', customer_id: '', customer_text: '', customer_type: 'gewerbe', project_id: '', notes: '', due_date: '', items: [emptyItem()], materials: [] });

export default function Finanzen() {
  const { API } = useAuth();
  const [docs, setDocs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [mainTab, setMainTab] = useState('rechnung');
  const [subTab, setSubTab] = useState('offen');
  const [showCreate, setShowCreate] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [sourceAngebot, setSourceAngebot] = useState(null);
  const [settings, setSettings] = useState(null);
  const [matSearch, setMatSearch] = useState('');
  const [matResults, setMatResults] = useState([]);
  const [showMatSearch, setShowMatSearch] = useState(false);
  const [showTplSearch, setShowTplSearch] = useState(false);
  const [tplSearch, setTplSearch] = useState('');
  const [tplResults, setTplResults] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showReminderLog, setShowReminderLog] = useState(false);
  const [reminderLogDoc, setReminderLogDoc] = useState(null);
  const [reminderLog, setReminderLog] = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentDoc, setPaymentDoc] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, date: '', method: '', notes: '' });
  const [payments, setPayments] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailDoc, setEmailDoc] = useState(null);
  const [emailType, setEmailType] = useState('invoice');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [itemMatSearch, setItemMatSearch] = useState({ idx: -1, query: '', results: [] });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/financial-documents`), axios.get(`${API}/customers`),
      axios.get(`${API}/projects`), axios.get(`${API}/settings`)
    ]).then(([d, c, p, s]) => {
      setDocs(d.data); setCustomers(c.data); setProjects(p.data); setSettings(s.data);
    });
  }, [API]);

  const angebote = docs.filter(d => d.type === 'angebot');

  const openCreate = (type = 'rechnung', fromAngebot = null) => {
    if (fromAngebot) {
      setSourceAngebot(fromAngebot);
      setForm({
        type,
        customer_id: fromAngebot.customer_id || '',
        customer_text: fromAngebot.customer_text || '',
        project_id: fromAngebot.project_id || '',
        notes: fromAngebot.notes || '',
        due_date: '',
        materials: fromAngebot.materials || [],
        items: (fromAngebot.items || []).map(i => ({
          description: i.description,
          quantity: i.quantity || 0,
          unit: i.unit || 'Stk.',
          unit_price: i.unit_price || 0,
          tax_rate: i.tax_rate || settings?.default_tax_rate || 19,
          parent_pos: i.parent_pos ?? null,
          sub_pos: i.sub_pos ?? null,
          is_text: i.is_text || false,
        })),
      });
    } else {
      setSourceAngebot(null);
      setForm({ ...emptyForm(), type, items: [{ description: '', quantity: 1, unit: 'Stk.', unit_price: 0, tax_rate: settings?.default_tax_rate || 19 }] });
    }
    setShowCreate(true);
  };

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setSourceAngebot(null);
    setForm({
      type: doc.type,
      customer_id: doc.customer_id || '',
      customer_text: doc.customer_text || '',
      project_id: doc.project_id || '',
      notes: doc.notes || '',
      due_date: doc.due_date || '',
      materials: doc.materials || [],
      items: (doc.items || []).map(i => ({
        description: i.description || '', quantity: i.quantity || 0, unit: i.unit || 'Stk.',
        unit_price: i.unit_price || 0, tax_rate: i.tax_rate || 19,
        parent_pos: i.parent_pos ?? null, sub_pos: i.sub_pos ?? null, is_text: i.is_text || false,
      })),
    });
    setShowCreate(true);
  };

  const handleCreate = async () => {
    try {
      const payload = { ...form };
      if (payload.customer_id === '_text' || !payload.customer_id) {
        payload.customer_id = null;
      }
      if (!payload.customer_text) delete payload.customer_text;

      let res;
      if (editingDoc) {
        res = await axios.put(`${API}/financial-documents/${editingDoc.id}`, payload);
        setDocs(prev => prev.map(d => d.id === editingDoc.id ? res.data : d));
        toast.success('Angebot aktualisiert');
      } else {
        if (sourceAngebot) payload.source_angebot_id = sourceAngebot.id;
        res = await axios.post(`${API}/financial-documents`, payload);
        setDocs(prev => [res.data, ...prev]);
        toast.success(`${TYPE_LABELS[form.type]} erstellt`);
      }
      setShowCreate(false);
      setForm(emptyForm());
      setSourceAngebot(null);
      setEditingDoc(null);
    } catch { toast.error('Fehler'); }
  };

  const addItem = () => setForm({ ...form, items: [...form.items, emptyItem(settings?.default_tax_rate || 19)] });
  const addTextItem = () => setForm({ ...form, items: [...form.items, emptyTextItem()] });
  const addSubItem = (parentIdx) => {
    const items = [...form.items];
    // Find main position number for this parent
    let mainPos = 0;
    for (let j = 0; j <= parentIdx; j++) {
      if (items[j].parent_pos === null || items[j].parent_pos === undefined) mainPos++;
    }
    // Count existing sub-items for this parent
    let subCount = 0;
    for (let j = parentIdx + 1; j < items.length; j++) {
      if (items[j].parent_pos === mainPos) subCount++;
      else if (items[j].parent_pos === null || items[j].parent_pos === undefined) break;
    }
    const subItem = { ...emptyItem(settings?.default_tax_rate || 19), parent_pos: mainPos, sub_pos: subCount + 1 };
    // Insert after the parent and existing subs
    let insertIdx = parentIdx + 1;
    while (insertIdx < items.length && items[insertIdx].parent_pos === mainPos) insertIdx++;
    items.splice(insertIdx, 0, subItem);
    setForm({ ...form, items });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: value };
    setForm({ ...form, items });
  };

  const searchMaterials = async (q) => {
    setMatSearch(q);
    if (q.length < 2) { setMatResults([]); return; }
    try {
      const res = await axios.get(`${API}/materials?search=${encodeURIComponent(q)}`);
      setMatResults(res.data);
    } catch { setMatResults([]); }
  };

  const addMaterialAsItem = (mat) => {
    const newItem = { description: mat.name + (mat.article_number ? ` (Art. ${mat.article_number})` : ''), quantity: 1, unit: mat.unit || 'Stk.', unit_price: mat.unit_price || 0, tax_rate: mat.tax_rate || 19 };
    setForm({ ...form, items: [...form.items, newItem] });
    setShowMatSearch(false); setMatSearch(''); setMatResults([]);
    toast.success(`${mat.name} hinzugefügt`);
  };

  const searchTemplates = async (q) => {
    setTplSearch(q);
    if (q.length < 2) { setTplResults([]); return; }
    try {
      const res = await axios.get(`${API}/invoice-templates?search=${encodeURIComponent(q)}`);
      setTplResults(res.data);
    } catch { setTplResults([]); }
  };

  const addTemplateAsItem = (tpl) => {
    // Add position with title + then description as text row below
    const posItem = {
      description: tpl.title, quantity: 1,
      unit: tpl.unit || 'Stk.', unit_price: tpl.unit_price || 0,
      tax_rate: tpl.tax_rate || 19, parent_pos: null, sub_pos: null, is_text: false
    };
    const newItems = [...form.items, posItem];
    // Add description as text line if it exists and differs from title
    if (tpl.description && tpl.description !== tpl.title) {
      newItems.push({
        description: tpl.description, quantity: 0, unit: '', unit_price: 0,
        tax_rate: 19, parent_pos: null, sub_pos: null, is_text: true
      });
    }
    setForm({ ...form, items: newItems });
    setShowTplSearch(false); setTplSearch(''); setTplResults([]);
    toast.success(`"${tpl.title}" hinzugefügt`);
  };

  const searchItemMaterial = async (idx, q) => {
    setItemMatSearch({ idx, query: q, results: [] });
    if (q.length < 2) return;
    try {
      const res = await axios.get(`${API}/materials?search=${encodeURIComponent(q)}`);
      setItemMatSearch(prev => ({ ...prev, results: res.data }));
    } catch {}
  };

  const addMaterialToItem = (mat) => {
    setForm(prev => ({
      ...prev,
      materials: [...(prev.materials || []), { id: mat.id, name: mat.name, description: mat.description, manufacturer: mat.manufacturer, article_number: mat.article_number, unit: mat.unit, unit_price: mat.unit_price, has_photo: mat.has_photo, photo_s3_key: mat.photo_s3_key, photo_path: mat.photo_path, category: mat.category }]
    }));
    setItemMatSearch({ idx: -1, query: '', results: [] });
    toast.success(`Material "${mat.name}" hinzugefügt`);
  };

  const removeMaterial = (matIdx) => {
    setForm(prev => ({ ...prev, materials: (prev.materials || []).filter((_, i) => i !== matIdx) }));
  };

  const handleDownload = (doc) => window.open(`${API}/financial-documents/${doc.id}/pdf`, '_blank');

  const handlePreview = async (doc) => {
    setPreviewDoc(doc);
    try {
      const res = await axios.get(`${API}/financial-documents/${doc.id}/pdf`, { responseType: 'blob' });
      setPreviewUrl(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })));
    } catch { toast.error('Vorschau konnte nicht geladen werden'); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const handleEmail = (doc, type = 'invoice') => {
    setEmailDoc(doc);
    setEmailType(type);
    const customer = customers.find(c => c.id === doc.customer_id);
    setEmailRecipient(customer?.email || '');
    setShowEmailDialog(true);
  };

  const handleSendEmail = async () => {
    if (!emailRecipient || !emailRecipient.includes('@')) { toast.error('Bitte gültige E-Mail-Adresse eingeben'); return; }
    setEmailSending(true);
    try {
      const res = await axios.post(`${API}/email/send`, {
        document_id: emailDoc.id, email_type: emailType, recipient: emailRecipient,
      });
      toast.success(`E-Mail an ${emailRecipient} gesendet`);
      setShowEmailDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally { setEmailSending(false); }
  };

  const handleReminder = async (doc) => {
    handleEmail(doc, 'reminder');
  };

  const handleDunning = async (doc) => {
    handleEmail(doc, 'dunning');
  };

  const openReminderLog = async (doc) => {
    setReminderLogDoc(doc);
    try {
      const res = await axios.get(`${API}/financial-documents/${doc.id}/reminders`);
      setReminderLog(res.data);
    } catch { setReminderLog([]); }
    setShowReminderLog(true);
  };

  const openPayment = async (doc) => {
    setPaymentDoc(doc);
    const todayLocal = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
    setPaymentForm({ amount: Math.max(0, (doc.total || 0) - (doc.paid_amount || 0)), date: todayLocal, method: 'Überweisung', notes: '' });
    try {
      const res = await axios.get(`${API}/financial-documents/${doc.id}/payments`);
      setPayments(res.data);
    } catch { setPayments([]); }
    setShowPayment(true);
  };

  const handlePayment = async () => {
    try {
      await axios.post(`${API}/financial-documents/${paymentDoc.id}/payments`, paymentForm);
      const [pRes, dRes] = await Promise.all([
        axios.get(`${API}/financial-documents/${paymentDoc.id}/payments`),
        axios.get(`${API}/financial-documents/${paymentDoc.id}`)
      ]);
      setPayments(pRes.data);
      setDocs(prev => prev.map(d => d.id === paymentDoc.id ? dRes.data : d));
      setPaymentDoc(dRes.data);
      toast.success('Zahlung erfasst');
    } catch { toast.error('Fehler'); }
  };

  const getPaymentStatusBadge = (doc) => {
    if (doc.type !== 'rechnung') return null;
    const status = doc.payment_status || 'offen';
    const colors = { offen: 'bg-slate-400/10 text-slate-400 border-slate-400/20', bezahlt: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20', teilbezahlt: 'bg-amber-400/10 text-amber-400 border-amber-400/20' };
    const labels = { offen: 'Offen', bezahlt: 'Bezahlt', teilbezahlt: 'Teilbezahlt' };
    const todayCheck = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
    const isOverdue = status === 'offen' && doc.due_date && doc.due_date < todayCheck;
    if (isOverdue) return <Badge className="bg-rose-400/10 text-rose-400 border-rose-400/20 rounded-sm text-[10px]">Überfällig</Badge>;
    return <Badge className={`${colors[status]} rounded-sm text-[10px]`}>{labels[status]}</Badge>;
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/financial-documents/${id}`);
    setDocs(prev => prev.map(d => d.id === id ? { ...d, deleted: true } : d));
    toast.success('In Papierkorb verschoben');
  };

  const handleRestore = async (id) => {
    const res = await axios.put(`${API}/financial-documents/${id}/restore`);
    setDocs(prev => prev.map(d => d.id === id ? { ...res.data, deleted: false } : d));
    toast.success('Wiederhergestellt');
  };

  const handleOfferStatus = async (doc, status) => {
    const res = await axios.put(`${API}/financial-documents/${doc.id}/offer-status`, { offer_status: status });
    setDocs(prev => prev.map(d => d.id === doc.id ? res.data : d));
    toast.success(`Status: ${OFFER_STATUS[status]}`);
  };

  const handleExport = (type) => window.open(`${API}/export/${type}`, '_blank');

  const filtered = (() => {
    if (mainTab === 'geloescht') {
      return docs.filter(d => d.deleted && d.type === subTab);
    }
    let list = docs.filter(d => !d.deleted && d.type === mainTab);
    if (mainTab === 'rechnung') {
      if (subTab === 'offen') list = list.filter(d => !d.payment_status || d.payment_status === 'offen' || d.payment_status === 'teilbezahlt');
      else if (subTab === 'bezahlt') list = list.filter(d => d.payment_status === 'bezahlt');
      else if (subTab === 'storniert') list = list.filter(d => d.payment_status === 'storniert');
    } else if (mainTab === 'angebot') {
      if (subTab === 'offen') list = list.filter(d => !d.offer_status || d.offer_status === 'entwurf' || d.offer_status === 'gesendet' || d.offer_status === 'verhandlung');
      else if (subTab === 'angenommen') list = list.filter(d => d.offer_status === 'angenommen');
      else if (subTab === 'abgelehnt') list = list.filter(d => d.offer_status === 'abgelehnt');
    } else if (mainTab === 'gutschrift') {
      if (subTab === 'offen') list = list.filter(d => !d.payment_status || d.payment_status === 'offen');
      else if (subTab === 'abgerechnet') list = list.filter(d => d.payment_status === 'bezahlt' || d.payment_status === 'abgerechnet');
    }
    return list;
  })();
  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || 'Unbekannt';

  return (
    <div className="space-y-4 animate-fade-in" data-testid="finanzen-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white tracking-tight">Finanzen</h1>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => handleExport('financial-documents')}
            className="bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 rounded-sm" data-testid="export-csv">
            <FileDown className="w-4 h-4 mr-1" />CSV Export
          </Button>
          <Button onClick={() => openCreate()} data-testid="create-financial-doc"
            className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]">
            <Plus className="w-4 h-4 mr-2" />Neues Dokument
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'rechnung', label: 'Rechnungen' },
          { key: 'angebot', label: 'Angebote' },
          { key: 'gutschrift', label: 'Gutschriften' },
          { key: 'geloescht', label: 'Gelöscht' },
        ].map(t => (
          <Button key={t.key} variant={mainTab === t.key ? 'default' : 'secondary'} size="sm"
            onClick={() => { setMainTab(t.key); setSubTab(t.key === 'rechnung' ? 'offen' : t.key === 'angebot' ? 'offen' : t.key === 'gutschrift' ? 'offen' : 'rechnung'); }}
            className={mainTab === t.key ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}>
            {t.label}
          </Button>
        ))}
      </div>

      {/* Sub-Tabs */}
      {mainTab !== 'geloescht' && (
        <div className="flex gap-1.5 flex-wrap">
          {mainTab === 'rechnung' && ['offen', 'bezahlt', 'storniert'].map(s => (
            <Button key={s} variant={subTab === s ? 'default' : 'ghost'} size="sm"
              onClick={() => setSubTab(s)}
              className={`text-xs ${subTab === s ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
              {s === 'offen' ? 'Offen' : s === 'bezahlt' ? 'Bezahlt' : 'Storniert'}
            </Button>
          ))}
          {mainTab === 'angebot' && ['offen', 'angenommen', 'abgelehnt'].map(s => (
            <Button key={s} variant={subTab === s ? 'default' : 'ghost'} size="sm"
              onClick={() => setSubTab(s)}
              className={`text-xs ${subTab === s ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
              {s === 'offen' ? 'Offen' : s === 'angenommen' ? 'Angenommen' : 'Abgelehnt'}
            </Button>
          ))}
          {mainTab === 'gutschrift' && ['offen', 'abgerechnet'].map(s => (
            <Button key={s} variant={subTab === s ? 'default' : 'ghost'} size="sm"
              onClick={() => setSubTab(s)}
              className={`text-xs ${subTab === s ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
              {s === 'offen' ? 'Offen' : 'Abgerechnet'}
            </Button>
          ))}
        </div>
      )}
      {mainTab === 'geloescht' && (
        <div className="flex gap-1.5 flex-wrap">
          {['rechnung', 'angebot', 'gutschrift'].map(s => (
            <Button key={s} variant={subTab === s ? 'default' : 'ghost'} size="sm"
              onClick={() => setSubTab(s)}
              className={`text-xs ${subTab === s ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
              {TYPE_LABELS[s]}
            </Button>
          ))}
        </div>
      )}

      <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-900/80 border-b border-slate-800/40">
              <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Nr.</th>
              <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Kunde</th>
              <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Typ</th>
              <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider text-right">Betrag</th>
              <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Datum</th>
              <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Zahlung</th>
              <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Mahnung</th>
              <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length ? filtered.map(d => (
              <tr key={d.id} className="border-b border-slate-800/40 hover:bg-slate-900/80 transition-colors" data-testid={`fin-doc-${d.id}`}>
                <td className="px-4 py-3 text-sm font-mono text-amber-400 whitespace-nowrap">{d.doc_number}</td>
                <td className="px-4 py-3 text-sm text-slate-300">{d.customer_text ? d.customer_text.split('\n')[0] : getCustomerName(d.customer_id)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <Badge className={`${TYPE_COLORS[d.type] || ''} rounded-sm text-xs`}>{TYPE_LABELS[d.type]}</Badge>
                    {d.type === 'angebot' && (
                      <select value={d.offer_status || 'entwurf'} onChange={e => handleOfferStatus(d, e.target.value)}
                        className="bg-transparent border-0 text-[10px] text-slate-400 cursor-pointer focus:outline-none">
                        {Object.entries(OFFER_STATUS).map(([k, v]) => <option key={k} value={k} className="bg-slate-900">{v}</option>)}
                      </select>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-mono text-white text-right whitespace-nowrap">{d.total?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR</td>
                <td className="px-4 py-3 text-xs font-mono text-slate-500 whitespace-nowrap">{d.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {getPaymentStatusBadge(d)}
                    {d.type === 'rechnung' && d.payment_status !== 'bezahlt' && (
                      <button onClick={() => openPayment(d)} className="text-emerald-400 hover:text-emerald-300" title="Zahlung erfassen">
                        <Banknote className="w-4 h-4" />
                      </button>
                    )}
                    {d.type === 'rechnung' && d.payment_status === 'bezahlt' && (
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {(d.reminder_count > 0 || d.dunning_count > 0) ? (
                      <button onClick={() => openReminderLog(d)} className="flex items-center gap-1 hover:opacity-80">
                        {d.reminder_count > 0 && <Badge className="bg-amber-400/10 text-amber-400 border-amber-400/20 rounded-sm text-[10px]"><Clock className="w-3 h-3 mr-0.5" />{d.reminder_count}x</Badge>}
                        {d.dunning_count > 0 && <Badge className="bg-rose-400/10 text-rose-400 border-rose-400/20 rounded-sm text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />{d.dunning_count}x</Badge>}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-700">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-0.5 justify-end">
                    {d.deleted ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => handleRestore(d.id)}
                          className="text-emerald-400 hover:bg-emerald-400/10 text-xs" title="Wiederherstellen">
                          <RotateCcw className="w-4 h-4 mr-1" />Wiederherstellen
                        </Button>
                      </>
                    ) : (
                      <>
                        {d.type === 'angebot' && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => openEdit(d)}
                              className="text-slate-400 hover:text-amber-400 w-8 h-8" title="Bearbeiten"><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => openCreate('rechnung', d)}
                              className="text-emerald-400 hover:bg-emerald-400/10 w-8 h-8" title="In Rechnung umwandeln"><ArrowRight className="w-4 h-4" /></Button>
                          </>
                        )}
                        {d.type === 'rechnung' && (
                          <>
                            {d.payment_status !== 'bezahlt' && (
                              <Button variant="ghost" size="icon" onClick={() => openPayment(d)}
                                className="text-emerald-400 hover:bg-emerald-400/10 w-8 h-8" title="Zahlung erfassen"><Banknote className="w-4 h-4" /></Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => handleReminder(d)}
                              className="text-slate-400 hover:text-amber-400 w-8 h-8" title="Zahlungserinnerung"><Clock className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDunning(d)}
                              className="text-slate-400 hover:text-rose-400 w-8 h-8" title="Mahnung"><AlertTriangle className="w-4 h-4" /></Button>
                          </>
                        )}
                        {(d.reminder_count > 0 || d.dunning_count > 0) && (
                          <Button variant="ghost" size="icon" onClick={() => openReminderLog(d)}
                            className="text-slate-400 hover:text-violet-400 w-8 h-8" title="Verlauf"><History className="w-4 h-4" /></Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handlePreview(d)} className="text-slate-400 hover:text-violet-400 w-8 h-8" title="Vorschau"><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDownload(d)} className="text-slate-400 hover:text-amber-400 w-8 h-8" title="PDF"><Download className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEmail(d)} className="text-slate-400 hover:text-blue-400 w-8 h-8" title="E-Mail"><Mail className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(d.id)} className="text-slate-400 hover:text-rose-400 w-8 h-8" title="Löschen"><Trash2 className="w-4 h-4" /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={8} className="p-8 text-center text-slate-500">
                {mainTab === 'geloescht' ? 'Papierkorb ist leer' : 'Keine Dokumente in diesem Bereich'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Convert Dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) { setSourceAngebot(null); setEditingDoc(null); setForm(emptyForm()); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingDoc ? `Angebot ${editingDoc.doc_number} bearbeiten` : sourceAngebot ? `Angebot ${sourceAngebot.doc_number} in Rechnung umwandeln` : 'Neues Finanzdokument'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">

            {/* Source Angebot info */}
            {sourceAngebot && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-3 flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-emerald-400 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-sm text-emerald-400 font-medium">Aus Angebot übernommen: {sourceAngebot.doc_number}</p>
                  <p className="text-xs text-slate-400">{sourceAngebot.items?.length || 0} Positionen · {sourceAngebot.total?.toFixed(2)} EUR Brutto · Kunde: {getCustomerName(sourceAngebot.customer_id)}</p>
                </div>
              </div>
            )}

            {/* Type + Angebot selector (only when not converting) */}
            {!sourceAngebot && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300">Dokumenttyp</Label>
                    <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="fin-type-select" className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k} className="text-slate-200">{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Kunde</Label>
                    <Select value={form.customer_id || '_text'} onValueChange={v => {
                      if (v === '_text') setForm({ ...form, customer_id: '', customer_text: form.customer_text || '' });
                      else setForm({ ...form, customer_id: v, customer_text: '' });
                    }}>
                      <SelectTrigger data-testid="fin-customer-select" className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="_text" className="text-slate-400 italic">Freitext eingeben...</SelectItem>
                        {customers.map(c => <SelectItem key={c.id} value={c.id} className="text-slate-200">{c.name}{c.company ? ` (${c.company})` : ''}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {(!form.customer_id || form.customer_id === '_text') && (
                      <>
                        <textarea value={form.customer_text || ''} onChange={e => setForm({ ...form, customer_text: e.target.value, customer_id: '' })}
                          placeholder={"Firma XY GmbH\nMax Mustermann\nMusterstr. 1\n10115 Berlin"}
                          rows={4} data-testid="customer-freetext"
                          className="w-full mt-2 bg-slate-950 border border-slate-800 rounded-sm text-sm text-slate-200 placeholder:text-slate-600 p-2 resize-y focus:border-amber-500 focus:outline-none" />
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => setForm({...form, customer_type: 'gewerbe'})}
                            className={`flex-1 py-1.5 px-3 rounded-sm text-xs font-medium border transition-colors ${form.customer_type === 'gewerbe' ? 'border-amber-500 bg-amber-500/10 text-amber-400' : 'border-slate-800 text-slate-500'}`}>
                            Gewerbekunde
                          </button>
                          <button onClick={() => setForm({...form, customer_type: 'privat'})}
                            className={`flex-1 py-1.5 px-3 rounded-sm text-xs font-medium border transition-colors ${form.customer_type === 'privat' ? 'border-amber-500 bg-amber-500/10 text-amber-400' : 'border-slate-800 text-slate-500'}`}>
                            Privatkunde
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Quick convert from Angebot */}
                {angebote.length > 0 && form.type !== 'angebot' && (
                  <div className="bg-slate-950/60 border border-blue-500/20 rounded-sm p-3">
                    <Label className="text-blue-400 flex items-center gap-2 mb-2 text-xs">
                      <FileCheck className="w-3.5 h-3.5" />Positionen aus Angebot übernehmen
                    </Label>
                    <Select value="none" onValueChange={v => {
                      if (v !== 'none') {
                        const ang = angebote.find(a => a.id === v);
                        if (ang) openCreate(form.type, ang);
                      }
                    }}>
                      <SelectTrigger data-testid="convert-angebot-select" className="bg-slate-950 border-slate-800 text-slate-200">
                        <SelectValue placeholder="Angebot auswählen (optional)..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="none" className="text-slate-400">Ohne Vorlage</SelectItem>
                        {angebote.map(a => (
                          <SelectItem key={a.id} value={a.id} className="text-slate-200">
                            <span className="font-mono text-blue-400 mr-2">{a.doc_number}</span>
                            {getCustomerName(a.customer_id)} ({a.total?.toFixed(2)} EUR)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {sourceAngebot && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Dokumenttyp</Label>
                  <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="rechnung" className="text-slate-200">Rechnung</SelectItem>
                      <SelectItem value="gutschrift" className="text-slate-200">Gutschrift</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Kunde</Label>
                  <Select value={form.customer_id || '_text'} onValueChange={v => {
                    if (v === '_text') setForm({ ...form, customer_id: '', customer_text: form.customer_text || '' });
                    else setForm({ ...form, customer_id: v, customer_text: '' });
                  }}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="_text" className="text-slate-400 italic">Freitext...</SelectItem>
                      {customers.map(c => <SelectItem key={c.id} value={c.id} className="text-slate-200">{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {(!form.customer_id) && (
                    <textarea value={form.customer_text || ''} onChange={e => setForm({ ...form, customer_text: e.target.value, customer_id: '' })}
                      placeholder={"Firma\nName\nAdresse"} rows={3}
                      className="w-full mt-2 bg-slate-950 border border-slate-800 rounded-sm text-sm text-slate-200 placeholder:text-slate-600 p-2 resize-y focus:border-amber-500 focus:outline-none" />
                  )}
                </div>
              </div>
            )}

            <div>
              <Label className="text-slate-300">Fälligkeitsdatum</Label>
              <Input data-testid="fin-due-date" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-slate-300">Positionen</Label>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => { setShowTplSearch(!showTplSearch); setShowMatSearch(false); }}
                    className="text-violet-400 hover:text-violet-300" data-testid="search-template-btn">
                    <BookText className="w-4 h-4 mr-1" />Posten
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setShowMatSearch(!showMatSearch); setShowTplSearch(false); }}
                    className="text-blue-400 hover:text-blue-300" data-testid="search-material-btn">
                    <Layers className="w-4 h-4 mr-1" />Material
                  </Button>
                  <Button variant="ghost" size="sm" onClick={addTextItem} className="text-slate-400 hover:text-slate-200" data-testid="add-text-item">
                    <AlignLeft className="w-4 h-4 mr-1" />Text
                  </Button>
                  <Button variant="ghost" size="sm" onClick={addItem} className="text-amber-400 hover:text-amber-300" data-testid="add-line-item">
                    <Plus className="w-4 h-4 mr-1" />Position
                  </Button>
                </div>
              </div>

              {/* Material Search Inline */}
              {showMatSearch && (
                <div className="mb-3 bg-slate-950/60 border border-blue-500/20 rounded-sm p-3">
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
                    <Input value={matSearch} onChange={e => searchMaterials(e.target.value)} autoFocus
                      placeholder="Material, Artikelnr., Hersteller..." data-testid="material-search-input"
                      className="pl-10 bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 focus:border-blue-500" />
                  </div>
                  {matResults.length > 0 && (
                    <div className="max-h-[180px] overflow-y-auto space-y-1">
                      {matResults.map(m => (
                        <button key={m.id} onClick={() => addMaterialAsItem(m)} data-testid={`mat-pick-${m.id}`}
                          className="w-full flex items-center gap-3 px-3 py-2 bg-slate-900/50 border border-slate-800/40 rounded-sm hover:border-blue-500/40 hover:bg-blue-500/5 transition-colors text-left">
                          {m.has_photo ? (
                            <img src={`${API}/materials/${m.id}/photo`} alt="" className="w-8 h-8 rounded-sm object-cover shrink-0" />
                          ) : (
                            <div className="w-8 h-8 rounded-sm bg-slate-800 flex items-center justify-center shrink-0"><Layers className="w-3 h-3 text-slate-600" /></div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-200 truncate">{m.name}</p>
                            <p className="text-[10px] text-slate-500">{m.category}{m.manufacturer ? ` · ${m.manufacturer}` : ''}</p>
                          </div>
                          <span className="text-xs font-mono text-amber-400 shrink-0">{m.unit_price?.toFixed(2)} EUR/{m.unit}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {matSearch.length >= 2 && matResults.length === 0 && (
                    <p className="text-xs text-slate-500 text-center py-2">Keine Materialien gefunden</p>
                  )}
                </div>
              )}
              {/* Template Search Inline */}
              {showTplSearch && (
                <div className="mb-3 bg-slate-950/60 border border-violet-500/20 rounded-sm p-3">
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-violet-400" />
                    <Input value={tplSearch} onChange={e => searchTemplates(e.target.value)} autoFocus
                      placeholder="Rechnungsposten suchen..." data-testid="template-search-input"
                      className="pl-10 bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 focus:border-violet-500" />
                  </div>
                  {tplResults.length > 0 && (
                    <div className="max-h-[200px] overflow-y-auto space-y-1">
                      {tplResults.map(t => (
                        <button key={t.id} onClick={() => addTemplateAsItem(t)} data-testid={`tpl-pick-${t.id}`}
                          className="w-full flex items-center gap-3 px-3 py-2 bg-slate-900/50 border border-slate-800/40 rounded-sm hover:border-violet-500/40 hover:bg-violet-500/5 transition-colors text-left">
                          <div className="w-8 h-8 rounded-sm bg-violet-500/10 border border-violet-500/20 flex items-center justify-center shrink-0">
                            <BookText className="w-4 h-4 text-violet-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-200 font-medium">{t.title}</p>
                            <p className="text-[10px] text-slate-500 line-clamp-1">{t.description}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-xs font-mono text-amber-400">{t.unit_price?.toFixed(2)} EUR</span>
                            <p className="text-[10px] text-slate-600">/ {t.unit}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {tplSearch.length >= 2 && tplResults.length === 0 && (
                    <p className="text-xs text-slate-500 text-center py-2">Keine Posten gefunden</p>
                  )}
                </div>
              )}
              {form.items.map((item, i) => {
                const isSub = item.parent_pos !== null && item.parent_pos !== undefined;
                const isText = item.is_text;

                // Text-only row
                if (isText) {
                  return (
                    <div key={i} className="flex gap-2 mb-2 items-start bg-slate-950/30 border border-dashed border-slate-700/50 rounded-sm p-2" data-testid={`line-item-${i}`}>
                      <AlignLeft className="w-4 h-4 text-slate-500 mt-2 shrink-0" />
                      <textarea value={item.description} onChange={e => updateItem(i, 'description', e.target.value)}
                        placeholder="Hinweis, Ausschluss oder Erklärung eingeben..."
                        rows={2} className="flex-1 bg-transparent border-0 text-sm text-slate-400 italic placeholder:text-slate-600 resize-y focus:outline-none focus:ring-0 p-0" />
                      <button onClick={() => removeItem(i)} className="text-slate-500 hover:text-rose-400 mt-1 shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                }

                // Calculate position label
                let posLabel;
                if (isSub) {
                  posLabel = `${item.parent_pos}.${item.sub_pos}`;
                } else {
                  let mainCount = 0;
                  for (let j = 0; j <= i; j++) {
                    if ((form.items[j].parent_pos === null || form.items[j].parent_pos === undefined) && !form.items[j].is_text) mainCount++;
                  }
                  posLabel = String(mainCount);
                }
                return (
                <div key={i} className={`grid grid-cols-[40px_1fr_70px_70px_90px_32px] gap-2 mb-2 items-end ${isSub ? 'ml-6' : ''}`} data-testid={`line-item-${i}`}>
                  <div>
                    {i === 0 && <Label className="text-xs text-slate-500">Pos.</Label>}
                    <div className={`h-9 flex items-center justify-center text-xs font-mono ${isSub ? 'text-slate-500' : 'text-amber-400 font-bold'}`}>{posLabel}</div>
                  </div>
                  <div>
                    {i === 0 && <Label className="text-xs text-slate-500">Beschreibung</Label>}
                    <Input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)}
                      placeholder={isSub ? 'Unterposition...' : 'Leistung/Material'} className="bg-slate-950 border-slate-800 text-slate-200 text-sm placeholder:text-slate-600" />
                  </div>
                  <div>
                    {i === 0 && <Label className="text-xs text-slate-500">Menge</Label>}
                    <Input type="number" value={item.quantity} onChange={e => updateItem(i, 'quantity', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-slate-200 text-sm" />
                  </div>
                  <div>
                    {i === 0 && <Label className="text-xs text-slate-500">Einheit</Label>}
                    <Input value={item.unit} onChange={e => updateItem(i, 'unit', e.target.value)}
                      className="bg-slate-950 border-slate-800 text-slate-200 text-sm" />
                  </div>
                  <div>
                    {i === 0 && <Label className="text-xs text-slate-500">Preis</Label>}
                    <Input type="number" value={item.unit_price} onChange={e => updateItem(i, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-slate-200 text-sm" />
                  </div>
                  <div className="flex gap-0.5">
                    {!isSub && (
                      <button onClick={() => addSubItem(i)} title="Unterposition hinzufügen"
                        className="h-9 w-5 flex items-center justify-center text-blue-400 hover:text-blue-300 text-xs font-bold">+</button>
                    )}
                    {form.items.length > 1 && (
                      <button onClick={() => removeItem(i)} className="h-9 w-5 flex items-center justify-center text-slate-500 hover:text-rose-400">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
              <div className="text-right mt-2 space-y-1">
                <div><span className="text-sm text-slate-400">Netto: </span><span className="text-sm font-mono text-white">{form.items.filter(i => !i.is_text).reduce((s, i) => s + i.quantity * i.unit_price, 0).toFixed(2)} EUR</span></div>
                <div><span className="text-sm text-slate-400">Brutto: </span><span className="text-sm font-mono text-amber-400 font-bold">{(form.items.filter(i => !i.is_text).reduce((s, i) => s + i.quantity * i.unit_price, 0) * 1.19).toFixed(2)} EUR</span></div>
              </div>
            </div>

            {/* Materialliste */}
            <div className="bg-slate-950/50 border border-emerald-500/20 rounded-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-emerald-400 flex items-center gap-1.5 text-xs">
                  <Layers className="w-3.5 h-3.5" />Materialliste ({(form.materials || []).length})
                </Label>
              </div>
              {/* Search */}
              {itemMatSearch.idx >= 0 ? (
                <div className="mb-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-emerald-400" />
                    <Input value={itemMatSearch.query} onChange={e => searchItemMaterial(itemMatSearch.idx, e.target.value)} autoFocus
                      placeholder="Material suchen..." className="pl-9 bg-slate-950 border-slate-800 text-slate-200 text-sm placeholder:text-slate-600 focus:border-emerald-500 h-8" />
                    <button onClick={() => setItemMatSearch({ idx: -1, query: '', results: [] })} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
                  </div>
                  {itemMatSearch.results.length > 0 && (
                    <div className="mt-1 max-h-[120px] overflow-y-auto space-y-0.5">
                      {itemMatSearch.results.map(m => (
                        <button key={m.id} onClick={() => addMaterialToItem(m)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 bg-slate-900/50 border border-slate-800/40 rounded-sm hover:border-emerald-500/40 text-left text-xs">
                          <span className="text-slate-200 truncate flex-1">{m.name}</span>
                          {m.manufacturer && <span className="text-slate-500">{m.manufacturer}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {itemMatSearch.query.length >= 2 && itemMatSearch.results.length === 0 && <p className="text-[10px] text-slate-500 mt-1">Keine Materialien gefunden</p>}
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setItemMatSearch({ idx: 0, query: '', results: [] })}
                  className="text-emerald-400 hover:text-emerald-300 text-xs mb-1" data-testid="add-material-to-doc">
                  <Plus className="w-3.5 h-3.5 mr-1" />Material hinzufügen
                </Button>
              )}
              {/* Added materials */}
              {(form.materials || []).length > 0 && (
                <div className="space-y-1 mt-1">
                  {(form.materials || []).map((m, mi) => (
                    <div key={mi} className="flex items-center justify-between px-2 py-1.5 bg-slate-900/40 rounded-sm border border-slate-800/30 text-xs">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Layers className="w-3 h-3 text-emerald-400 shrink-0" />
                        <span className="text-slate-200 truncate">{m.name}</span>
                        {m.manufacturer && <span className="text-slate-500">({m.manufacturer})</span>}
                      </div>
                      <button onClick={() => removeMaterial(mi)} className="text-slate-500 hover:text-rose-400 shrink-0 ml-2"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[9px] text-slate-600 mt-1">Materialien werden als Anhang-Seite im PDF aufgeführt</p>
            </div>

            <div>
              <Label className="text-slate-300">Anmerkungen</Label>
              <Textarea data-testid="fin-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Optionale Notizen..." className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <Button onClick={handleCreate} data-testid="create-fin-doc-submit" className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              {editingDoc ? 'Angebot speichern' : sourceAngebot ? `${TYPE_LABELS[form.type]} aus Angebot erstellen` : `${TYPE_LABELS[form.type] || 'Dokument'} erstellen`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={v => { if (!v) closePreview(); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-4xl h-[90vh] flex flex-col p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/60 shrink-0">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-violet-400" />
              <div>
                <h3 className="text-sm font-bold text-white">{TYPE_LABELS[previewDoc?.type]} {previewDoc?.doc_number}</h3>
                <p className="text-xs text-slate-500">{getCustomerName(previewDoc?.customer_id)} · {previewDoc?.total?.toFixed(2)} EUR</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => handleDownload(previewDoc)}
                className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm" data-testid="preview-download">
                <Download className="w-4 h-4 mr-1" />Herunterladen
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleEmail(previewDoc)}
                className="text-slate-400 hover:text-blue-400" data-testid="preview-email">
                <Mail className="w-4 h-4 mr-1" />E-Mail
              </Button>
              <Button size="sm" variant="ghost" onClick={closePreview} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="flex-1 bg-slate-950">
            {previewUrl ? (
              <iframe src={previewUrl} className="w-full h-full border-0" title="PDF Vorschau" data-testid="pdf-preview-iframe" />
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500 animate-pulse">PDF wird geladen...</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reminder/Dunning Log Dialog */}
      <Dialog open={showReminderLog} onOpenChange={v => { setShowReminderLog(v); if (!v) setReminderLogDoc(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-violet-400" />
              Mahnverlauf — {reminderLogDoc?.doc_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {reminderLogDoc && (
              <div className="bg-slate-950/50 rounded-sm p-3 border border-slate-800/40 flex items-center justify-between text-sm">
                <span className="text-slate-400">{getCustomerName(reminderLogDoc.customer_id)}</span>
                <span className="font-mono text-white">{reminderLogDoc.total?.toFixed(2)} EUR</span>
              </div>
            )}
            {reminderLog.length ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {reminderLog.map(r => (
                  <div key={r.id} className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-3" data-testid={`reminder-entry-${r.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <Badge className={`rounded-sm text-xs ${r.type === 'erinnerung' ? 'bg-amber-400/10 text-amber-400 border-amber-400/20' : 'bg-rose-400/10 text-rose-400 border-rose-400/20'}`}>
                        {r.type === 'erinnerung' ? 'Zahlungserinnerung' : 'Mahnung'}
                      </Badge>
                      <span className="text-xs font-mono text-slate-500">
                        {new Date(r.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        {' '}
                        {new Date(r.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">Erstellt von: {r.created_by_name}</p>
                    {r.notes && <p className="text-xs text-slate-500 mt-1">{r.notes}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">Noch keine Erinnerungen oder Mahnungen versendet.</p>
            )}

            <div className="flex gap-2 pt-2 border-t border-slate-800/40">
              <Button size="sm" onClick={() => { setShowReminderLog(false); handleReminder(reminderLogDoc); }}
                className="bg-amber-500/10 text-amber-400 border border-amber-400/20 hover:bg-amber-500/20 rounded-sm flex-1">
                <Clock className="w-4 h-4 mr-2" />Erinnerung senden
              </Button>
              <Button size="sm" onClick={() => { setShowReminderLog(false); handleDunning(reminderLogDoc); }}
                className="bg-rose-500/10 text-rose-400 border border-rose-400/20 hover:bg-rose-500/20 rounded-sm flex-1">
                <AlertTriangle className="w-4 h-4 mr-2" />Mahnung senden
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={v => { setShowPayment(v); if (!v) setPaymentDoc(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-400" />
              Zahlung erfassen — {paymentDoc?.doc_number}
            </DialogTitle>
          </DialogHeader>
          {paymentDoc && (
            <div className="space-y-4">
              <div className="bg-slate-950/50 rounded-sm p-3 border border-slate-800/40 grid grid-cols-3 gap-2 text-sm">
                <div><span className="text-slate-500 text-xs">Gesamt</span><p className="font-mono text-white">{paymentDoc.total?.toFixed(2)} EUR</p></div>
                <div><span className="text-slate-500 text-xs">Bezahlt</span><p className="font-mono text-emerald-400">{(paymentDoc.paid_amount || 0).toFixed(2)} EUR</p></div>
                <div><span className="text-slate-500 text-xs">Offen</span><p className="font-mono text-amber-400">{Math.max(0, (paymentDoc.total || 0) - (paymentDoc.paid_amount || 0)).toFixed(2)} EUR</p></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-300 text-xs">Betrag (EUR)</Label>
                <Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value) || 0})}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1 font-mono" /></div>
                <div><Label className="text-slate-300 text-xs">Datum</Label>
                <Input type="date" value={paymentForm.date} onChange={e => setPaymentForm({...paymentForm, date: e.target.value})}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-300 text-xs">Zahlungsart</Label>
                <Input value={paymentForm.method} onChange={e => setPaymentForm({...paymentForm, method: e.target.value})}
                  placeholder="Überweisung, Bar..." className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" /></div>
                <div><Label className="text-slate-300 text-xs">Notiz</Label>
                <Input value={paymentForm.notes} onChange={e => setPaymentForm({...paymentForm, notes: e.target.value})}
                  placeholder="Optional" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" /></div>
              </div>
              <Button onClick={handlePayment} className="w-full bg-emerald-500 text-white hover:bg-emerald-400 font-bold rounded-sm">
                <Banknote className="w-4 h-4 mr-2" />Zahlung erfassen
              </Button>

              {payments.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Bisherige Zahlungen</p>
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {payments.map(p => (
                      <div key={p.id} className="flex items-center justify-between py-2 px-3 bg-slate-950/50 rounded-sm border border-slate-800/40 text-xs">
                        <div>
                          <span className="font-mono text-emerald-400">{p.amount.toFixed(2)} EUR</span>
                          <span className="text-slate-500 ml-2">{p.method}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-mono text-slate-500">{p.date}</span>
                          <span className="text-slate-600 ml-2">{p.created_by_name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Send Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={v => { setShowEmailDialog(v); if (!v) setEmailDoc(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              {emailType === 'reminder' ? 'Zahlungserinnerung senden' : emailType === 'dunning' ? 'Mahnung senden' : 'Dokument per E-Mail senden'}
            </DialogTitle>
          </DialogHeader>
          {emailDoc && (
            <div className="space-y-4">
              <div className="bg-slate-950/50 rounded-sm p-3 border border-slate-800/40">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-400 font-mono">{emailDoc.doc_number}</span>
                  <Badge className={`${TYPE_COLORS[emailDoc.type] || ''} rounded-sm text-xs`}>{TYPE_LABELS[emailDoc.type]}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1">{emailDoc.total?.toFixed(2)} EUR · {emailDoc.customer_text?.split('\n')[0] || getCustomerName(emailDoc.customer_id)}</p>
              </div>
              <div>
                <Label className="text-slate-300">Empfänger E-Mail</Label>
                <Input type="email" value={emailRecipient} onChange={e => setEmailRecipient(e.target.value)}
                  placeholder="kunde@firma.de" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
              </div>
              <div className="bg-slate-950/30 border border-slate-800/30 rounded-sm p-3 text-xs text-slate-500 space-y-1">
                <p>PDF wird automatisch als Anhang beigefügt.</p>
                <p>E-Mail-Vorlage und Signatur aus Einstellungen → E-Mail Versand.</p>
                {emailType === 'reminder' && <p className="text-amber-400">Zahlungserinnerung wird zusätzlich im Mahnverlauf protokolliert.</p>}
                {emailType === 'dunning' && <p className="text-rose-400">Mahnung wird zusätzlich im Mahnverlauf protokolliert.</p>}
              </div>
              <Button onClick={async () => {
                if (emailType === 'reminder') {
                  await axios.post(`${API}/financial-documents/${emailDoc.id}/reminders`, { type: 'erinnerung' });
                  setDocs(prev => prev.map(d => d.id === emailDoc.id ? { ...d, reminder_count: (d.reminder_count || 0) + 1 } : d));
                } else if (emailType === 'dunning') {
                  await axios.post(`${API}/financial-documents/${emailDoc.id}/reminders`, { type: 'mahnung' });
                  setDocs(prev => prev.map(d => d.id === emailDoc.id ? { ...d, dunning_count: (d.dunning_count || 0) + 1 } : d));
                }
                await handleSendEmail();
              }} disabled={emailSending}
                className="w-full bg-blue-500 text-white hover:bg-blue-400 font-bold rounded-sm">
                <Mail className="w-4 h-4 mr-2" />{emailSending ? 'Wird gesendet...' : 'E-Mail senden'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
